import React, { useEffect, useState } from 'react'
import { Slider } from 'sutra/components/Slider/Slider'

import {
  PaneContainer,
  PaneValueLabel,
  RatingSliderWrap,
} from 'features/charts-v2/components/filter-popover-pane.styles'
import { FilterPopoverPaneFooter } from 'features/charts-v2/components/FilterPopoverPaneFooter/FilterPopoverPaneFooter'

interface NumericRange {
  min?: number
  max?: number
}

interface FilterPopoverRangePaneProps {
  // The values the field actually spans: the slider's extent.
  bounds: NumericRange
  // Currently-committed window; an absent side is open-ended.
  committed: NumericRange
  onApply: (range: NumericRange) => void
  onClear: () => void
}

const defined = (...values: (number | undefined)[]): number[] =>
  values.filter((v): v is number => v !== undefined)

// Always cents: whole-number bounds say nothing about the values between them
// (a 1–200 price field still holds 105.99). Never the backend's `step`, which
// is sized for star ratings and skips real values.
const STEP = 0.01

// Snap to cents so float drift never reaches the label or the filter.
const snap = (value: number): number => Number(value.toFixed(2))

// In-popover editor for a numeric custom field (e.g. a price): a range slider
// over the field's real bounds. A handle left at its end leaves that side
// open, so values beyond today's bounds still match later. Nothing writes
// until Apply.
export const FilterPopoverRangePane: React.FC<FilterPopoverRangePaneProps> = ({
  bounds,
  committed,
  onApply,
  onClear,
}) => {
  // A committed window can sit outside bounds narrowed by other filters;
  // widen the extent so its handles stay on the track.
  const lo = Math.min(...defined(bounds.min, committed.min), Infinity)
  const hi = Math.max(...defined(bounds.max, committed.max), -Infinity)
  const extentMin = Number.isFinite(lo) ? lo : 0
  const extentMax = Number.isFinite(hi) ? hi : extentMin

  // One mark when the extent collapses to a point, so marks never share a value.
  const endMarks =
    extentMin === extentMax
      ? [{ value: extentMin, label: `${extentMin}` }]
      : [
          { value: extentMin, label: `${extentMin}` },
          { value: extentMax, label: `${extentMax}` },
        ]

  const [draftMin, setDraftMin] = useState(committed.min)
  const [draftMax, setDraftMax] = useState(committed.max)

  // Re-sync the draft when the committed filter changes externally.
  useEffect(() => {
    setDraftMin(committed.min)
    setDraftMax(committed.max)
  }, [committed.min, committed.max])

  const shownMin = draftMin ?? extentMin
  const shownMax = draftMax ?? extentMax
  const isOpen = draftMin === undefined && draftMax === undefined
  const isUnchanged = draftMin === committed.min && draftMax === committed.max

  return (
    <PaneContainer>
      <PaneValueLabel>
        {shownMin} – {shownMax}
      </PaneValueLabel>
      <RatingSliderWrap>
        <Slider
          min={extentMin}
          max={extentMax}
          step={STEP}
          value={[shownMin, shownMax]}
          thumbLabels={['Minimum', 'Maximum']}
          marks={endMarks}
          // Only the moved handle changes its side: a saved bound outside the
          // field's bounds IS the track's end, and re-reading the untouched
          // handle there would wrongly open that side.
          onChange={(values) => {
            const [nextMin = extentMin, nextMax = extentMax] = values.map(snap)
            if (nextMin !== shownMin) {
              setDraftMin(nextMin <= extentMin ? undefined : nextMin)
            }
            if (nextMax !== shownMax) {
              setDraftMax(nextMax >= extentMax ? undefined : nextMax)
            }
          }}
        />
      </RatingSliderWrap>
      <FilterPopoverPaneFooter
        applyDisabled={isOpen || isUnchanged}
        clearDisabled={
          committed.min === undefined && committed.max === undefined
        }
        // An open side is omitted, not sent as `undefined`.
        onApply={() =>
          onApply({
            ...(draftMin !== undefined && { min: draftMin }),
            ...(draftMax !== undefined && { max: draftMax }),
          })
        }
        onClear={onClear}
      />
    </PaneContainer>
  )
}
